/**
 * /account/recurring-visits — "Manage recurring visits" (migration 0096).
 *
 * Lists the signed-in customer's weekly daycare series and lets them stop
 * future visits. Single-visit changes go through the booking itself.
 */
import { type RecurringSeriesDto, formatDay, sessionLabel, weekdaysLabel } from '@/lib/recurrence';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { customerApi } from '../customer_api';

type SeriesRow = RecurringSeriesDto & { petName: string };

const muted = 'var(--muted-foreground, oklch(0.52 0.008 75))';
const border = '1px solid var(--border, oklch(0.915 0.005 80))';

function statusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'ended':
      return 'Ended';
    default:
      return status;
  }
}

export function RecurringVisits() {
  const location = useLocation();
  const justCreated = Boolean((location.state as { created?: boolean } | null)?.created);
  const [rows, setRows] = useState<SeriesRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () =>
    customerApi<{ series: SeriesRow[] }>('/api/customer/recurring-series')
      .then((d) => setRows(d.series))
      .catch(() => setError('Could not load your recurring visits. Please try again.'));

  useEffect(() => {
    void load();
  }, []);

  const stop = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await customerApi(`/api/customer/recurring-series/${id}/cancel-future`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setConfirmId(null);
      await load();
    } catch {
      setError('Could not stop this series. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const live = (rows ?? []).filter((r) => r.status !== 'ended');
  const ended = (rows ?? []).filter((r) => r.status === 'ended');

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <span
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: muted,
          }}
        >
          Daycare
        </span>
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 0.75rem',
          }}
        >
          Recurring visits
        </h1>
        <p style={{ margin: '0 0 1.5rem', color: muted, fontSize: '0.95rem' }}>
          Weekly daycare rules. We keep the next 12 weeks booked and add more automatically.{' '}
          <Link to="/account/bookings" style={{ color: 'var(--primary, #004AAD)' }}>
            All bookings
          </Link>
        </p>

        {justCreated && (
          <p
            role="status"
            style={{
              margin: '0 0 1.5rem',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              background: 'var(--primary-soft, oklch(0.43 0.18 263 / 0.08))',
              color: 'var(--primary, #004AAD)',
              fontSize: '0.9rem',
            }}
          >
            Your weekly visits are booked. Dates that were full have been skipped — you can book
            those individually.
          </p>
        )}

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}
        {!error && rows === null && <p style={{ color: muted }}>Loading…</p>}

        {rows !== null && live.length === 0 && (
          <div
            style={{
              marginTop: '1rem',
              padding: '2.5rem 1.5rem',
              borderRadius: '14px',
              border,
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, color: muted, fontSize: '0.95rem' }}>
              No recurring visits set up.
            </p>
            <Link
              to="/book?category=daycare"
              style={{
                display: 'inline-block',
                marginTop: '1rem',
                color: 'var(--primary, #004AAD)',
                fontSize: '0.9rem',
              }}
            >
              Book daycare
            </Link>
          </div>
        )}

        {live.length > 0 && (
          <div style={{ borderTop: border }}>
            {live.map((s) => (
              <div key={s.id} style={{ borderBottom: border, padding: '1rem 0' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: '1rem',
                  }}
                >
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>
                      {s.petName} · {weekdaysLabel(s.weekdays)}
                    </p>
                    <p style={{ margin: '0.25rem 0 0', color: muted, fontSize: '0.85rem' }}>
                      {sessionLabel(s.sessionType)} · from {formatDay(s.startDate, true)}
                      {s.endDate ? ` until ${formatDay(s.endDate, true)}` : ''}
                      {s.materialisedThrough
                        ? ` · booked through ${formatDay(s.materialisedThrough, true)}`
                        : ''}
                    </p>
                  </div>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      color: muted,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {statusLabel(s.status)}
                  </span>
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem' }}>
                  {confirmId === s.id ? (
                    <span style={{ fontSize: '0.85rem', color: muted }}>
                      Stop all upcoming visits in this series?{' '}
                      <button
                        type="button"
                        onClick={() => stop(s.id)}
                        disabled={busyId === s.id}
                        style={{
                          color: 'var(--destructive, oklch(0.55 0.15 30))',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        Yes, stop
                      </button>{' '}
                      ·{' '}
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          color: muted,
                        }}
                      >
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(s.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        color: 'var(--destructive, oklch(0.55 0.15 30))',
                      }}
                    >
                      Stop future visits
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {ended.length > 0 && (
          <details style={{ marginTop: '1.5rem' }}>
            <summary style={{ cursor: 'pointer', color: muted, fontSize: '0.85rem' }}>
              Ended series ({ended.length})
            </summary>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0' }}>
              {ended.map((s) => (
                <li
                  key={s.id}
                  style={{
                    padding: '0.5rem 0',
                    color: muted,
                    fontSize: '0.85rem',
                    borderBottom: border,
                  }}
                >
                  {s.petName} · {weekdaysLabel(s.weekdays)} · {sessionLabel(s.sessionType)} ·{' '}
                  {formatDay(s.startDate, true)}
                  {s.endDate ? ` – ${formatDay(s.endDate, true)}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </main>
  );
}
